import type { CheckInOutStudent, StudentNote } from "../types/checkinout";
import type { AttendanceTodayData } from "./attendanceDataAdapter";

export const mapTodayDataToStudent = (
  row: AttendanceTodayData,
): CheckInOutStudent => {
  const statusUiRaw = (row as any).status_ui as string | null | undefined;
  const statusFromUi: CheckInOutStudent["status"] | undefined = (() => {
    switch (statusUiRaw) {
      case "anwesend":
      case "überfällig":
      case "abwesend":
      case "entlassen":
      case "beurlaubt":
      case "entschuldigt":
      case "klärungsbedarf":
        return statusUiRaw;
      default:
        return undefined;
    }
  })();

  const presence = row.presence_status ?? null;
  let status: CheckInOutStudent["status"];
  if (statusFromUi) {
    status = statusFromUi;
  } else {
    switch (presence) {
      case "present":
      case "late":
      case "partial":
        status = "anwesend";
        break;
      case "absent_excused":
        status = "entschuldigt";
        break;
      case "absent_unexcused":
        status = "abwesend";
        break;
      case "left_early":
      case "left_without_notice":
        status = "entlassen";
        break;
      default:
        status = "abwesend";
        break;
    }
  }

  const toTime = (ts: string | null): string | undefined => {
    if (!ts) return undefined;
    try {
      const s = String(ts);
      // Match HH:MM at start
      const direct = s.match(/^(\d{1,2}):(\d{2})/);
      if (direct) {
        return `${direct[1].padStart(2, "0")}:${direct[2]}`;
      }
      // Match time part after T or space (ISO / DB timestamps)
      // Use regex only — no Date objects — to avoid timezone conversion
      const isoMatch = s.match(/[T ](\d{1,2}):(\d{2})/);
      if (isoMatch) {
        return `${isoMatch[1].padStart(2, "0")}:${isoMatch[2]}`;
      }
    } catch {}
    return undefined;
  };

  const pickup = row.pickup ?? {
    type: "Abholung",
    notes: null,
    is_fallback: false,
    has_weekly_rule: false,
    has_daily_override: false,
  };
  const basePickup = pickup.type || "Abholung";
  const pickupNotes = (pickup.notes ?? "").toString().trim();
  const pickupRule = pickupNotes ? `${basePickup}: ${pickupNotes}` : basePickup;

  const primaryGuardian = (() => {
    if (!Array.isArray(row.guardians) || row.guardians.length === 0)
      return undefined;
    const sorted = [...row.guardians].sort((a, b) => {
      if (a.is_primary_guardian !== b.is_primary_guardian) {
        return a.is_primary_guardian ? -1 : 1;
      }
      if (a.is_primary_contact !== b.is_primary_contact) {
        return a.is_primary_contact ? -1 : 1;
      }
      const aprio = a.pickup_priority ?? Number.MAX_SAFE_INTEGER;
      const bprio = b.pickup_priority ?? Number.MAX_SAFE_INTEGER;
      if (aprio !== bprio) return aprio - bprio;
      return 0;
    });
    return sorted[0];
  })();

  const guardianName = primaryGuardian
    ? `${primaryGuardian.first_name || ""} ${
        primaryGuardian.last_name || ""
      }`.trim() || undefined
    : undefined;
  const guardianRelationship = primaryGuardian?.relationship || undefined;

  const visiblePhones = (() => {
    if (!primaryGuardian || !Array.isArray(primaryGuardian.contacts))
      return [] as string[];
    const phones = primaryGuardian.contacts.filter((c) => {
      if (c.type !== "phone") return false;
      if (c.visible === false) return false;
      const st = (c.status || "").toString().toLowerCase();
      if (st === "invalid") return false;
      return !!(c.value && String(c.value).trim());
    });
    return phones.map((c) => String(c.value).trim()).filter(Boolean);
  })();

  const guardianPhone = visiblePhones[0] || undefined;

  const contacts: CheckInOutStudent["contacts"] = (() => {
    const list: CheckInOutStudent["contacts"] = [];
    for (const g of row.guardians || []) {
      const name = [g.first_name, g.last_name].filter(Boolean).join(" ").trim();
      const relationship = g.relationship || "";
      const phones: string[] = [];
      if (Array.isArray(g.contacts)) {
        for (const c of g.contacts) {
          if (c.type !== "phone") continue;
          const st = (c.status || "").toString().toLowerCase();
          if (st === "invalid") continue;
          const phoneVal = String(c.value || "").trim();
          if (!phoneVal) continue;
          phones.push(phoneVal);
        }
      }
      const primaryPhone = phones[0] || "";
      list.push({
        name,
        relationship,
        phone: primaryPhone,
        phones: phones.length ? phones : undefined,
        adultId: g.profile_id,
        authorized: g.authorized_for_pickup ?? undefined,
        isPrimary:
          g.is_primary_guardian || g.is_primary_contact ? true : undefined,
        pickupPriority: g.pickup_priority ?? null,
        className: undefined,
      });
    }
    return list.length ? list : undefined;
  })();

  const baseNotes: StudentNote[] = (() => {
    const raw = (row.notes ?? "").toString().trim();
    if (!raw) return [];
    // Filter out internal metadata tags used by backend RPCs
    const isMetadataTag = (s: string) =>
      /^class_absence:/i.test(s) || /^method:/i.test(s);
    const extractReadable = (s: string) => {
      const m = s.match(/^(?:diary|reason):(.+)$/i);
      return m ? m[1].trim() : s;
    };
    // Parse checkout:<StaffName>:<text> tagged notes
    const parseCheckoutTag = (s: string): { author: string; text: string } | null => {
      const m = s.match(/^checkout:([^:]+):(.+)$/i);
      if (m) return { author: m[1].trim(), text: m[2].trim() };
      return null;
    };
    // Parse note:<Author>:<DD.MM.YYYY HH:MM>:<body> tagged notes —
    // emitted by fn_attendance_list_minimal for rows from
    // student_attendance_notes (Fehlzeit-modal "Grund" comments on orphan
    // / Flex-Klärung entries).
    const parseNoteTag = (
      s: string,
    ): { author: string; timestamp: string; text: string } | null => {
      const m = s.match(
        /^note:([^:]+):(\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}):(.+)$/i,
      );
      if (m)
        return {
          author: m[1].trim(),
          timestamp: m[2].trim(),
          text: m[3].trim(),
        };
      return null;
    };
    const lines = raw
      .split(/[\r\n;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((s) => !isMetadataTag(s));
    return lines
      .map((line, idx) => {
        // Try structured checkout tag first
        const tagged = parseCheckoutTag(line);
        if (tagged) {
          return {
            id: `${row.student_id}-today-${idx}`,
            text: `Checkout-Notiz: ${tagged.text}`,
            timestamp: "",
            author: tagged.author,
          };
        }
        // Fehlzeit attendance-thread notes
        const noted = parseNoteTag(line);
        if (noted) {
          return {
            id: `${row.student_id}-today-${idx}`,
            text: noted.text,
            timestamp: noted.timestamp,
            author: noted.author,
          };
        }
        // Extract readable content from diary/reason tags
        const readable = extractReadable(line);
        if (!readable) return null;
        return {
          id: `${row.student_id}-today-${idx}`,
          text: readable,
          timestamp: "",
          author: "System",
        };
      })
      .filter(Boolean) as StudentNote[];
  })();

  return {
    id: row.student_id,
    firstName: row.first_name,
    lastName: row.last_name,
    class: row.class_name || "",
    classId: row.class_id || undefined,
    course: row.flex_course || undefined,
    status,
    checkInTime: toTime(row.check_in_time),
    checkInStaff: row.check_in_by_name || undefined,
    checkInById: row.check_in_by || undefined,
    checkOutTime: toTime(row.check_out_time),
    checkOutStaff: row.check_out_by_name || undefined,
    checkOutById: row.check_out_by || undefined,
    checkOutMethod: row.check_out_method || undefined,
    pickupRule,
    pickupIsFallback: !!pickup.is_fallback,
    specialRuleToday: pickup.has_daily_override ? pickupRule : undefined,
    expectedDismissal: row.expected_dismissal_time || undefined,
    hasFlexModule: !!row.has_flex_today,
    importantInfoText: row.important_info || undefined,
    notes: baseNotes,
    hasRestriction: false,
    hasMedicalNote: false,
    parentContact: guardianName || "",
    emergencyContact: undefined,
    guardianName,
    guardianRelationship,
    guardianPhone,
    contactsPlain: undefined,
    contacts,
    roomName: row.flex_room_name || undefined,
    roomNumber: row.flex_room_number || undefined,
    guardiansRaw: Array.isArray(row.guardians) ? row.guardians : undefined,
    canViewPhones: row.can_view_phones,
    contactAccessExpiresAt: row.contact_access_expires_at || undefined,
    hasPhoneNumbers: (row as any).has_phone_numbers ?? undefined,
  };
};
